import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";

/**
 * VoiceInput — a small pill mic button. Records audio in the browser,
 * sends it to /api/voice/transcribe, and calls onTranscript(text).
 * Uses Wispr Flow if the backend has WISPR_API_KEY, else Whisper.
 */
export default function VoiceInput({ onTranscript, className = "", size = "sm", disabled = false, testId = "voice-input" }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => () => {
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
  }, []);

  const start = async () => {
    if (disabled || busy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        setBusy(true);
        try {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const resp = await fetch(`${API}/voice/transcribe`, { method: "POST", body: fd, credentials: "include" });
          if (!resp.ok) throw new Error("Transcription failed");
          const data = await resp.json();
          const text = (data?.text || "").trim();
          if (text) { onTranscript?.(text); }
          else toast.error("Didn't catch that — try again");
        } catch (e) { toast.error("Voice transcription failed"); }
        finally {
          setBusy(false);
          streamRef.current?.getTracks().forEach(t => t.stop());
        }
      };
      mr.start();
      mrRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  const stop = () => {
    if (mrRef.current && recording) {
      mrRef.current.stop();
      setRecording(false);
    }
  };

  const toggle = () => (recording ? stop() : start());

  const sizeClass = size === "lg" ? "w-11 h-11" : "w-8 h-8";
  const iconSize = size === "lg" ? "w-5 h-5" : "w-4 h-4";

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled || busy}
      title={recording ? "Stop recording" : "Speak"}
      data-testid={testId}
      aria-label={recording ? "Stop recording" : "Voice input"}
      className={`${sizeClass} shrink-0 rounded-full grid place-items-center transition-all ${recording ? "bg-pink text-white animate-pulse" : "bg-white border text-neutral-500 hover:text-pink hover:border-pink"} ${busy ? "opacity-60" : ""} ${className}`}
    >
      {busy ? <Loader2 className={`${iconSize} animate-spin`} /> : (recording ? <MicOff className={iconSize} /> : <Mic className={iconSize} />)}
    </button>
  );
}
